import axios from 'axios';

const MAX_TEXT_LENGTH = 80;

function slugifyPart(value, fallback = 'unknown') {
  const text = (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return text || fallback;
}

function getRouteSection(pathname) {
  const segment = slugifyPart((pathname || '').split('/').filter(Boolean)[0], 'app');
  if (segment.startsWith('campaign')) return 'campaign';
  if (segment.startsWith('leads')) return 'leads';
  if (segment.startsWith('ai')) return 'ai';
  if (segment === 'settings') return 'settings';
  if (segment === 'user_activity') return 'user_activity';
  return segment;
}

function getRouteTabOrPage(pathname) {
  const segment = (pathname || '').split('/').filter(Boolean)[0] || 'home';
  return slugifyPart(segment, 'home');
}

function getElementField(element) {
  const explicitField = element?.dataset?.eventField;
  if (explicitField) return slugifyPart(explicitField, 'button');

  const label =
    element?.getAttribute('aria-label') ||
    element?.name ||
    element?.id ||
    element?.textContent ||
    'button';
  return slugifyPart(label.slice(0, MAX_TEXT_LENGTH), 'button');
}

function getElementEvent(element) {
  const explicitAction = element?.dataset?.eventAction;
  if (explicitAction) return slugifyPart(explicitAction, 'click');

  if (element?.dataset?.trackCard === 'true') return 'click_card';
  if (element?.tagName === 'A') return 'navigate';
  if (element?.getAttribute('type') === 'submit') return 'submit';
  return 'click';
}

export function buildAgentInteractionEventName({ pathname, element }) {
  const section = getRouteSection(pathname);
  const tabOrPage =
    slugifyPart(
      element?.closest('[data-event-modal]')?.getAttribute('data-event-modal') ||
        element?.closest('[data-event-page]')?.getAttribute('data-event-page') ||
        element?.closest('[data-event-tab]')?.getAttribute('data-event-tab') ||
        getRouteTabOrPage(pathname),
      'unknown_page'
    );

  const field = getElementField(element);
  const eventAction = getElementEvent(element);

  return `${section}.${tabOrPage}.${field}.${eventAction}`;
}

export function buildAgentPageVisitEventName(pathname) {
  const section = getRouteSection(pathname);
  const tabOrPage = getRouteTabOrPage(pathname);
  return `${section}.${tabOrPage}.page.visit`;
}

export function trackAgentEvent({ eventName, metadata = {} }) {
  // Defer all work — including localStorage reads, payload construction
  // and axios setup — to a macrotask. This guarantees the call site
  // (typically a click capture handler or a route-change effect) returns
  // immediately and never adds synchronous latency to user interactions.
  // The previous "fire-and-forget" pattern still ran axios.post()'s
  // synchronous request setup inline, which was noticeable on heavy
  // pages like /contacts.
  const scheduler =
    typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
      ? (cb) => window.requestIdleCallback(cb, { timeout: 500 })
      : (cb) => setTimeout(cb, 0);

  scheduler(() => {
    try {
      const jwtToken = localStorage.getItem('jwt_token');

      // Ensure param_token is captured from URL if present (e.g. email link clicks)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      if (urlToken) {
        localStorage.setItem('param_token', urlToken);
      }

      const paramToken = localStorage.getItem('param_token');

      axios.post(
        `${window.api_host}/save-settings`,
        {
          event_name: eventName,
          event_stream: 'agent_platform',
          param_token: paramToken,
          source: window.location.href,
          metadata,
        },
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      ).catch(error => {
        console.debug('Event tracking failed', error?.message || error);
      });
    } catch (error) {
      console.debug('Event tracking failed', error?.message || error);
    }
  });
}

export function buildAgentButtonEventName(args) {
  return buildAgentInteractionEventName(args);
}

export function trackAgentButtonEvent(args) {
  return trackAgentEvent(args);
}
